import React, { useEffect, useState } from "react";
import axios from "../../api/axiosConfig";
import {
  FaPlus,
  FaEdit,
  FaToggleOn,
  FaToggleOff,
  FaArrowLeft,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import CategoriaModal from "./components/CategoriaModal";

export default function CategoriaList() {
  const navigate = useNavigate();

  const [categorias, setCategorias] = useState([]);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);

  const [categoriaActual, setCategoriaActual] = useState({
    id: null,
    nombre: "",
    orden: 0,
    activo: true,
    imagen: null,      // string (ruta backend)
    imagenFile: null,  // File (solo frontend)
  });

  /* ===============================
     API
  =============================== */
  const cargarCategorias = async () => {
    try {
      const res = await axios.get("/categorias");
      const data = res.data || [];

      // ordenar por orden (0 al final)
      const ordenadas = [...data].sort((a, b) => {
        if (a.orden === 0 && b.orden !== 0) return 1;
        if (b.orden === 0 && a.orden !== 0) return -1;
        if (a.orden !== b.orden) return a.orden - b.orden;
        return a.nombre.localeCompare(b.nombre);
      });

      setCategorias(ordenadas);
    } catch {
      toast.error("❌ Error cargando categorías");
    }
  };

  useEffect(() => {
    cargarCategorias();
  }, []);

  /* ===============================
     MODAL
  =============================== */
  const abrirNueva = () => {
    setCategoriaActual({
      id: null,
      nombre: "",
      orden: 0,
      activo: true,
      imagen: null,
      imagenFile: null,
    });
    setModoEdicion(false);
    setMostrarModal(true);
  };

  const abrirEditar = (cat) => {
    setCategoriaActual({
      id: cat.id,
      nombre: cat.nombre,
      orden: cat.orden,
      activo: cat.activo,
      imagen: cat.imagen || null, // 🔥 importante
      imagenFile: null,
    });
    setModoEdicion(true);
    setMostrarModal(true);
  };

  const actualizarCampo = (campo, valor) => {
    setCategoriaActual((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  /* ===============================
     CRUD (FormData 🔥)
  =============================== */
  const guardarCategoria = async () => {
    if (!categoriaActual.nombre) {
      toast.warning("⚠️ El nombre es obligatorio");
      return;
    }

    try {
      const formData = new FormData();

      const payload = {
        nombre: categoriaActual.nombre,
        orden: categoriaActual.orden,
        activo: categoriaActual.activo,
      };

      // JSON de la categoría
      formData.append(
        "categoria",
        new Blob([JSON.stringify(payload)], {
          type: "application/json",
        })
      );

      // Imagen opcional
      if (categoriaActual.imagenFile) {
        formData.append("imagen", categoriaActual.imagenFile);
      }

      if (modoEdicion && categoriaActual.id) {
        await axios.put(
          `/categorias/${categoriaActual.id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success("✅ Categoría actualizada");
      } else {
        await axios.post(
          "/categorias",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success("✅ Categoría creada");
      }

      setMostrarModal(false);
      cargarCategorias();
      navigate("/admin/productos");
    } catch (e) {
      console.error(e);
      toast.error("❌ Error guardando categoría");
    }
  };

  const toggleEstado = async (id) => {
    try {
      await axios.patch(`/categorias/${id}/estado`);
      toast.success("Estado actualizado");
      cargarCategorias();
    } catch {
      toast.error("❌ Error cambiando estado");
    }
  };

  /* ===============================
     RENDER
  =============================== */
  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white/95 text-slate-900 dark:bg-slate-900/90 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6">
        {/* Encabezado */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/productos")}
              className="text-slate-500 dark:text-slate-400 hover:text-amber-500"
            >
              <FaArrowLeft />
            </button>

            <h2 className="text-lg font-semibold text-amber-500 dark:text-amber-300">
              Gestión de Categorías
            </h2>
          </div>

          <button
            onClick={abrirNueva}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium"
          >
            <FaPlus />
            Nueva Categoría
          </button>
        </div>

        {/* Tabla */}
        <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 dark:bg-slate-950/80">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Nombre</th>
                <th className="px-4 py-2 text-center font-semibold">Orden</th>
                <th className="px-4 py-2 text-center font-semibold">Estado</th>
                <th className="px-4 py-2 text-center font-semibold">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {categorias.length > 0 ? (
                categorias.map((c) => (
                  <tr
                    key={c.id}
                    className="
                      odd:bg-white even:bg-slate-50
                      dark:odd:bg-slate-900/60 dark:even:bg-slate-900/40
                      hover:bg-slate-100 dark:hover:bg-slate-800
                      transition-colors
                    "
                  >
                    <td className="px-4 py-2 font-medium">
                      {c.nombre}
                    </td>

                    <td className="px-4 py-2 text-center">
                      {c.orden}
                    </td>

                    <td className="px-4 py-2 text-center">
                      <span
                        className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-semibold border ${
                          c.activo
                            ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/60"
                            : "bg-red-500/15 text-red-300 border-red-500/60"
                        }`}
                      >
                        {c.activo ? "Activa" : "Inactiva"}
                      </span>
                    </td>

                    <td className="px-4 py-2 text-center flex justify-center gap-2">
                      <button
                        onClick={() => abrirEditar(c)}
                        className="text-sky-500 hover:scale-110 transition"
                      >
                        <FaEdit />
                      </button>

                      <button
                        onClick={() => toggleEstado(c.id)}
                        className="hover:scale-110 transition"
                      >
                        {c.activo ? (
                          <FaToggleOn className="text-emerald-500 text-lg" />
                        ) : (
                          <FaToggleOff className="text-red-500 text-lg" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-4 text-center text-slate-500 dark:text-slate-400"
                  >
                    No hay categorías registradas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <CategoriaModal
        open={mostrarModal}
        modoEdicion={modoEdicion}
        categoria={categoriaActual}
        onChange={actualizarCampo}
        onClose={() => setMostrarModal(false)}
        onSave={guardarCategoria}
      />
    </>
  );
}
